// client/src/components/marketer/cps/modals/SettleModal.js
import React, { useState, useCallback } from 'react';
import SettleConfirmModal from './SettleConfirmModal';
import SettleCompleteModal from './SettleCompleteModal';

function SettleModal({ open, onClose, settlement, project, customerCounts, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showComplete, setShowComplete] = useState(false);
  
  // 증빙 파일 관련 state
  const [selectedFiles, setSelectedFiles] = useState([]); // File 객체 보관
  const [uploadError, setUploadError] = useState(null);
  
  // 진행 상태 (UX 개선)
  const [uploadProgress, setUploadProgress] = useState(null); // { status: 'uploading'|'uploaded'|'settling'|'completed', message: string }
  
  // 정산 완료 정보 저장 (완료 모달에서 사용)
  const [completedSettlementInfo, setCompletedSettlementInfo] = useState(null);

  // 파일 선택 핸들러 (메모리에만 보관)
  const handleFileSelect = useCallback((e) => {
    const files = Array.from(e.target.files || []);
    
    if (files.length === 0) return;
    
    if (selectedFiles.length + files.length > 10) {
      setUploadError('최대 10개까지만 업로드 가능합니다.');
      return;
    }

    // 파일 크기 체크 (클라이언트 단에서 미리)
    const totalSize = [...selectedFiles, ...files].reduce((sum, f) => sum + (f.size || 0), 0);
    const maxTotalBytes = 500 * 1024 * 1024; // 500MB
    
    if (totalSize > maxTotalBytes) {
      setUploadError(`전체 파일 크기가 500MB를 초과합니다. (현재: ${Math.round(totalSize / 1024 / 1024)}MB)`);
      return;
    }

    setUploadError(null);
    setSelectedFiles(prev => [...prev, ...files]);
  }, [selectedFiles]);

  // 파일 삭제
  const handleFileRemove = useCallback((index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  }, []);

  // 정산 실행 (업로드 + 정산)
  const handleSubmit = useCallback(async () => {
    setShowConfirm(false);
    setLoading(true);
    setError(null);
    setUploadProgress(null);
    
    try {
      if (selectedFiles.length === 0) {
        throw new Error('증빙 파일을 1개 이상 선택해주세요.');
      }

      // 🔄 STEP 1: 증빙 파일 업로드
      setUploadProgress({ status: 'uploading', message: '증빙 파일 업로드 중...' });
      
      const formData = new FormData();
      formData.append('projectId', project.id);
      formData.append('roundNumber', settlement.roundNumber);
      
      selectedFiles.forEach(file => {
        formData.append('files', file);
      });

      const uploadRes = await fetch('/api/marketer/cps-proof-upload', {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      const uploadData = await uploadRes.json();

      if (!uploadRes.ok || !uploadData.ok) {
        throw new Error(uploadData.message || '파일 업로드에 실패했습니다.');
      }

      // ✅ 업로드 완료
      setUploadProgress({ status: 'uploaded', message: '증빙 파일 업로드 완료 ✓' });

      // 🔄 STEP 2: 정산 처리
      setUploadProgress({ status: 'settling', message: '정산 처리 중...' });

      const proofFileKeys = uploadData.uploadedFiles.map(f => f.key);
      const settleItems = settlement.settlementData.map(data => ({
        influencerId: data.influencerId,
        customerCount: customerCounts[data.influencerId] || 0
      }));

      const settlementId = settlement._id || settlement.id;
      if (!settlementId) {
        throw new Error('Settlement ID를 찾을 수 없습니다.');
      }

      const res = await fetch(`/api/marketer/cps-settlements/${settlementId}/settle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          settlements: settleItems,
          proofFileKeys: proofFileKeys
        })
      });
      
      const data = await res.json();
      
      if (!res.ok || !data.ok) {
        throw new Error(data.message || '정산 처리에 실패했습니다.');
      }

      // ✅ 정산 완료
      setUploadProgress({ status: 'completed', message: '정산 완료 ✓' });
      
      // 총 지급액 계산
      const rewardPerBuyer = project.perPurchaseReward?.rewardPerBuyer || 0;
      const totalRewardAmount = settlement.settlementData.reduce((sum, data) => {
        if (data.rewardAmount > 0) return sum + data.rewardAmount;
        const count = customerCounts[data.influencerId] || 0;
        const amount = count * rewardPerBuyer;
        return sum + Math.min(amount, data.maxRewardAmount);
      }, 0);
      
      // 완료 정보 저장
      setCompletedSettlementInfo({
        roundNumber: settlement.roundNumber,
        targetCount: settlement.settlementData.length,
        totalAmount: totalRewardAmount
      });
      
      setTimeout(() => {
        setShowComplete(true); // 완료 모달 표시
      }, 500);
      
    } catch (e) {
      console.error('정산 처리 오류:', e);
      setError(e.message);
      setUploadProgress(null);
    } finally {
      setLoading(false);
    }
  }, [selectedFiles, settlement, project, customerCounts]);

  if (!open || !settlement || !project) return null;

  const rewardPerBuyer = project.perPurchaseReward?.rewardPerBuyer || 0;
  const totalRewardAmount = settlement.settlementData.reduce((sum, data) => {
    if (data.rewardAmount > 0) return sum + data.rewardAmount;
    const count = customerCounts[data.influencerId] || 0;
    const amount = count * rewardPerBuyer;
    return sum + Math.min(amount, data.maxRewardAmount);
  }, 0);

  return (
    <div className="mbm-overlay" style={{
      position: 'fixed',
      inset: 0,
      display: 'flex',
      justifyContent: 'center',
      zIndex: 1200
    }}>
      <div
        className="mb-card mbm"
        style={{ 
          width: '90vw',
          maxWidth: 600,
          maxHeight: '90vh',
          overflowY: 'auto',
          margin: 0,
          padding: '32px'
        }} 
        onClick={e => e.stopPropagation()}
      >
        {/* 헤더 */}
        <div className="mbm-head" style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: 24,
          paddingBottom: 16,
          borderBottom: '2px solid var(--mb-border-color)'
        }}>
          <h3 className="mbm-title" style={{ margin: 0 }}>
            💰 정산 진행하기
          </h3>
          <button
            onClick={onClose}
            disabled={loading}
            className="mbm-close"
            style={{
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.5 : 1
            }}
          >
            ×
          </button>
        </div>

        {/* 🔄 진행 상태 표시 (로딩 중일 때만) */}
        {loading && uploadProgress && (
          <div style={{
            padding: '20px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: 'var(--mb-card-radius)',
            marginBottom: 24,
            color: 'white',
            textAlign: 'center'
          }}>
            {/* 스피너 */}
            <div style={{
              width: 50,
              height: 50,
              border: '4px solid rgba(255,255,255,0.3)',
              borderTop: '4px solid white',
              borderRadius: '50%',
              margin: '0 auto 16px',
              animation: 'spin 1s linear infinite'
            }} />
            
            {/* 상태 메시지 */}
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>
              {uploadProgress.message}
            </div>
            
            {/* 단계 표시 */}
            <div style={{ fontSize: 13, opacity: 0.9 }}>
              {uploadProgress.status === 'uploading' && '1/2 단계'}
              {uploadProgress.status === 'uploaded' && '1/2 단계 완료'}
              {uploadProgress.status === 'settling' && '2/2 단계'}
              {uploadProgress.status === 'completed' && '완료'}
            </div>

            {/* 프로그레스 바 */}
            <div style={{
              marginTop: 16,
              height: 8,
              background: 'rgba(255,255,255,0.3)',
              borderRadius: 4,
              overflow: 'hidden'
            }}>
              <div style={{
                height: '100%',
                background: 'white',
                borderRadius: 4,
                transition: 'width 0.3s ease',
                width: uploadProgress.status === 'uploading' ? '50%' :
                       uploadProgress.status === 'uploaded' ? '50%' :
                       uploadProgress.status === 'settling' ? '75%' :
                       '100%'
              }} />
            </div>
          </div>
        )}
        
        {/* 정산 정보 */}
        <div style={{ 
          padding: '20px',
          background: '#f6f8fb',
          borderRadius: 'var(--mb-card-radius)',
          marginBottom: 24,
          border: '1px solid var(--mb-border-color)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12, alignItems: 'center' }}>
            <span style={{ color: 'var(--mb-text-sub)', fontSize: 14 }}>결산 회차</span>
            <span style={{ fontWeight: 700, color: 'var(--mb-text-title)', fontSize: 16 }}>
              {settlement.roundNumber}회차
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12, alignItems: 'center' }}>
            <span style={{ color: 'var(--mb-text-sub)', fontSize: 14 }}>결산 대상 인원</span>
            <span style={{ fontWeight: 700, color: 'var(--mb-text-title)', fontSize: 16 }}>
              {settlement.settlementData.length}명
            </span>
          </div>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingTop: 12, 
            marginTop: 12,
            borderTop: '2px solid var(--mb-border-color)'
          }}>
            <span style={{ color: 'var(--mb-text-title)', fontWeight: 600, fontSize: 15 }}>총 지급 포인트</span>
            <span style={{ color: 'var(--mb-color-primary)', fontWeight: 700, fontSize: 20 }}>
              {totalRewardAmount.toLocaleString()} pt
            </span>
          </div>
        </div>

        {/* 증빙 파일 선택 섹션 */}
        <div style={{ marginBottom: 24 }}>
          <h4 style={{ 
            fontSize: 15,
            fontWeight: 600,
            color: 'var(--mb-text-title)',
            marginBottom: 12
          }}>
            📎 증빙 파일 선택 <span style={{ color: '#dc2626', fontSize: 14 }}>*필수</span>
          </h4>
          
          {/* 파일 선택 버튼 */}
          <label style={{
            display: 'block',
            padding: '20px',
            border: '2px dashed var(--mb-border-color)',
            borderRadius: 'var(--mb-card-radius)',
            textAlign: 'center',
            cursor: loading ? 'not-allowed' : 'pointer',
            background: '#fafbfc',
            transition: 'all 0.2s',
            marginBottom: 12,
            opacity: loading ? 0.6 : 1
          }}>
            <input
              type="file"
              multiple
              accept="image/*,.pdf"
              onChange={handleFileSelect}
              disabled={loading}
              style={{ display: 'none' }}
            />
            <div style={{ fontSize: 32, marginBottom: 8 }}>📁</div>
            <div style={{ fontSize: 14, color: 'var(--mb-text-sub)', marginBottom: 4 }}>
              클릭하여 파일 선택
            </div>
            <div style={{ fontSize: 12, color: 'var(--mb-text-placeholder)' }}>
              이미지, PDF 파일 / 최대 10개 / 총 500MB 이하<br/>
              <span style={{ color: '#059669', fontWeight: 500 }}>정산 확정 시 업로드됩니다</span>
            </div>
          </label>

          {/* 업로드 에러 */}
          {uploadError && (
            <div style={{
              padding: '10px 14px',
              background: '#fee2e2',
              color: '#991b1b',
              borderRadius: 6,
              fontSize: 13,
              marginBottom: 12,
              border: '1px solid #fecaca'
            }}>
              ❌ {uploadError}
            </div>
          )}

          {/* 선택된 파일 목록 */}
          {selectedFiles.length > 0 && (
            <div style={{
              padding: '16px',
              background: 'white',
              border: '1px solid var(--mb-border-color)',
              borderRadius: 'var(--mb-card-radius)'
            }}>
              <div style={{ 
                fontSize: 13, 
                fontWeight: 600, 
                color: 'var(--mb-text-title)', 
                marginBottom: 10 
              }}>
                선택된 파일 ({selectedFiles.length}개)
              </div>
              {selectedFiles.map((file, idx) => (
                <div key={idx} style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '8px 12px',
                  background: '#f6f8fb',
                  borderRadius: 6,
                  marginBottom: 6,
                  fontSize: 13
                }}>
                  <div style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    📄 {file.name} <span style={{ color: 'var(--mb-text-placeholder)', fontSize: 11 }}>
                      ({Math.round(file.size / 1024)}KB)
                    </span>
                  </div>
                  <button
                    onClick={() => handleFileRemove(idx)}
                    disabled={loading}
                    style={{
                      marginLeft: 10,
                      padding: '4px 10px',
                      background: '#fee2e2',
                      color: '#991b1b',
                      border: 'none',
                      borderRadius: 4,
                      cursor: loading ? 'not-allowed' : 'pointer',
                      fontSize: 12,
                      fontWeight: 500,
                      opacity: loading ? 0.6 : 1
                    }}
                  >
                    삭제
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* 안내 문구 */}
        <div style={{ 
          padding: '16px 20px',
          background: '#fff3cd',
          border: '1px solid #ffc107',
          borderRadius: 'var(--mb-card-radius)',
          marginBottom: 24
        }}>
          <p style={{ 
            fontSize: 14, 
            color: '#856404',
            lineHeight: 1.6,
            margin: 0,
            fontWeight: 500
          }}>
            ⚠️ 입력하신 유입 고객 수에 따라 보증금(리워드 총 예산)에서 포인트가 차감되고 인플루언서에게 지급됩니다.<br/>
            <strong>정산 완료 후에는 수정이 불가능</strong>하니 신중하게 확인해주세요.
          </p>
        </div>
        
        {/* 에러 메시지 */}
        {error && (
          <div style={{ 
            padding: '14px 18px',
            background: '#fee2e2',
            color: '#991b1b',
            borderRadius: 'var(--mb-card-radius)',
            marginBottom: 24,
            fontSize: 14,
            fontWeight: 500,
            border: '1px solid #fecaca'
          }}>
            ❌ {error}
          </div>
        )}
        
        {/* 버튼 */}
        <div className="mbm-foot">
          <button 
            onClick={onClose} 
            className="mb-btn mb-btn-secondary"
            disabled={loading}
            style={{ padding: '10px 24px', fontSize: 15 }}
          >
            취소
          </button>
          <button 
            onClick={() => {
              if (selectedFiles.length === 0) {
                setError('증빙 파일을 1개 이상 선택해주세요.');
                return;
              }
              setShowConfirm(true);
            }} 
            className="mb-btn mb-btn-primary"
            disabled={loading || selectedFiles.length === 0}
            style={{ 
              padding: '10px 24px',
              fontSize: 15,
              fontWeight: 600,
              opacity: (loading || selectedFiles.length === 0) ? 0.5 : 1,
              cursor: (loading || selectedFiles.length === 0) ? 'not-allowed' : 'pointer'
            }}
          >
            {loading ? '처리 중...' : '정산 완료하기'}
          </button>
        </div>
      </div>

      {/* 확인 모달 */}
      <SettleConfirmModal
        open={showConfirm}
        onClose={() => !loading && setShowConfirm(false)}
        onConfirm={handleSubmit}
        settlementInfo={{
          roundNumber: settlement.roundNumber,
          targetCount: settlement.settlementData.length,
          totalAmount: totalRewardAmount
        }}
      />

      {/* 완료 모달 */}
      <SettleCompleteModal
        open={showComplete}
        onClose={() => {
          setShowComplete(false);
          onSuccess(); // 화면 새로고침
        }}
        settlementInfo={completedSettlementInfo}
      />

      {/* 🔄 CSS 애니메이션 */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default SettleModal;
